import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { TextInput } from 'components/FormUtils/TextInput'
import { TextareaInput } from 'components/FormUtils/TextareaInput'
import { PhoneInput } from 'components/FormUtils/PhoneInput'
import CreatableSelect from 'components/FormUtils/CreatableSelect'
import { CreatableTagSelector } from 'components/CreatableTagSelector'
import useApiService from 'services/api'
import { CONTACT_TITLES } from 'utils/constants'
import { ContactDefaultValues } from 'components/Modals/ContactModal/ContactModal'

/**
 * Wizard step: the contact's own details (no company — that's a later step).
 * Field set is extracted from ContactDetailsStep, minus the company
 * autocomplete / domain / category and the edit-only seniority/department.
 */
export default function ContactInfoStep() {
  const { control, setError } = useFormContext<ContactDefaultValues>()
  const api = useApiService()
  const { data: tagsOptions } = api.useGetTagsOptions(false, 'contacts')

  const contactTitles = useMemo(() => [...CONTACT_TITLES], [])

  const createNewTitle = (
    title: string
  ): Promise<{ label: string; value: string }> =>
    Promise.resolve({ label: title, value: title })

  return (
    <div className="flex flex-col gap-2 p-2">
      <div className="flex items-start w-full gap-2">
        <TextInput
          label="First Name"
          control={control}
          name="first_name"
          placeholder="First Name"
        />
        <TextInput
          control={control}
          name="last_name"
          placeholder="Last Name"
          label="Last Name"
        />
      </div>

      <div className="flex flex-col gap-2">
        <div className="md:flex items-start gap-2">
          <TextInput
            label="Work Email"
            control={control}
            name="email_work"
            placeholder="work@company.com"
          />
          <TextInput
            label="Personal Email"
            control={control}
            name="email_personal"
            placeholder="personal@example.com"
          />
          <TextInput
            label="Other Email"
            control={control}
            name="email_generic"
            placeholder="info@company.com"
          />
        </div>
        <div className="text-sm italic text-fg-secondary">
          Enter a phone number or email
        </div>
      </div>

      <PhoneInput
        name="phone_number"
        control={control}
        label="Phone number"
        extensionName="extension"
        optional
      />

      <div className="flex flex-col md:flex-row md:items-start md:gap-2.5">
        <TextInput
          control={control}
          name="url"
          label="Website"
          placeholder="https://www.example.com/"
          optional
        />
        <TextInput
          control={control}
          name="linkedin_url"
          label="LinkedIn"
          placeholder="https://www.linkedin.com/"
          optional
        />
      </div>

      <div className="flex flex-col gap-2.5">
        <div className="text-lg mt-3 font-medium text-fg">Contact Type</div>
        <div className="flex flex-col md:flex-row md:items-start gap-2.5">
          <CreatableSelect
            label="Title"
            optional
            control={control}
            name="title"
            placeholder="Title"
            options={contactTitles.map((title) => ({
              label: title,
              value: title,
            }))}
            onCreateOption={createNewTitle}
            className="flex-1 mt-0"
          />
          <CreatableTagSelector
            className="flex-1 mt-0"
            name="tags"
            control={control}
            placeholder="Labels"
            options={tagsOptions}
            setError={setError}
            tagParent="contacts"
            label="Labels"
            optional
          />
        </div>
      </div>

      <TextareaInput
        control={control}
        name="notes"
        label="Notes"
        placeholder="Notes"
        optional
        className="gap-1 mt-0"
      />
    </div>
  )
}
